import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, CircularProgress, IconButton, Tooltip } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import dayjs from 'dayjs';

import { TextFieldUi } from '../../../../ui/TextFieldUi/TextFieldUi';
import { SelectUi } from '../../../../ui/SelectUi/SelectUi';
import { DateInputUi } from '../../../../ui/DateInputUi/DateInputUi';
import { MultiSelectUi } from '../../../../ui/MultiSelectUi/MultiSelectUi';
import { ButtonUi } from '../../../../ui/ButtonUi/ButtonUi';
import { StudyCard } from '../../../../DetailPage/StudyCard/StudyCard';
import { StudyEditorCard } from './StudyEditorCard';

import { studiesTypes } from '../../../../../types/Studies/studiesSelects';
import styles from './ProfessionalStudiesTab.module.scss';
import { ProfessionalStudiesTabProps } from './types/ProfessionalStudiesTab.types';

export const ProfessionalStudiesTab: React.FC<ProfessionalStudiesTabProps> = ({
    editMode,
    studies,
    onStudiesChange,
    errors,
    handleOpenViewer,
    handleOpenConfirm,
    availableTags = [],
    loadingTags = false,
    errorTags = null,
}) => {
    const { t } = useTranslation();

    // Track which saved study (by uid) is expanded for inline editing
    const [expandedStudyUid, setExpandedStudyUid] = useState<string | null>(null);
    const toggleExpanded = (uid: string) =>
        setExpandedStudyUid(prev => (prev === uid ? null : uid));

    // Track which study is open in view mode (accordion)
    const [openStudyUid, setOpenStudyUid] = useState<string | null>(null);

    const activeIndices = (studies || []).map((s, idx) => (!s.delete ? idx : -1)).filter(idx => idx !== -1);

    const isFirstActive = (index: number) => activeIndices.indexOf(index) === 0;
    const isLastActive = (index: number) => activeIndices.indexOf(index) === activeIndices.length - 1;

    const handleMoveUp = (index: number) => {
        onStudiesChange?.(arr => {
            const indices = arr.map((s, idx) => (!s.delete ? idx : -1)).filter(idx => idx !== -1);
            const k = indices.indexOf(index);
            if (k <= 0) return arr;
            const newArr = [...arr];
            const prevIndex = indices[k - 1];
            const temp = newArr[index];
            newArr[index] = newArr[prevIndex];
            newArr[prevIndex] = temp;
            return newArr;
        });
    };

    const handleMoveDown = (index: number) => {
        onStudiesChange?.(arr => {
            const indices = arr.map((s, idx) => (!s.delete ? idx : -1)).filter(idx => idx !== -1);
            const k = indices.indexOf(index);
            if (k === -1 || k >= indices.length - 1) return arr;
            const newArr = [...arr];
            const nextIndex = indices[k + 1];
            const temp = newArr[index];
            newArr[index] = newArr[nextIndex];
            newArr[nextIndex] = temp;
            return newArr;
        });
    };

    // Count active studies
    const activeStudies = (studies || []).filter(s => !s.delete);
    const savedStudies = activeStudies.filter(s => !!s.uid);
    const newStudies = activeStudies.filter(s => !s.uid);

    return (
        <div className={styles['studies-tab']}>

            {!editMode ? (
                <div className={styles['studies-tab__list']}>
                    <h2 style={{ margin: '0 0 14px', fontSize: '26px', lineHeight: '1.08', letterSpacing: '-0.04em', fontWeight: '700', color: '#13231D' }}>
                        {t('landing.TabsComponent.studiesTitle', 'Estudios y certificaciones')}
                    </h2>
                    {studies && studies.length > 0 ? (
                        studies.map((study, index) => (
                            !study.delete && (
                                <StudyCard
                                    key={index}
                                    study={study}
                                    onViewCertificate={handleOpenViewer}
                                    isOpen={openStudyUid === (study.uid ?? `idx-${index}`)}
                                    onToggle={() => {
                                        const id = study.uid ?? `idx-${index}`;
                                        setOpenStudyUid(prev => (prev === id ? null : id));
                                    }}
                                />
                            )
                        ))
                    ) : (
                        <p className={styles['studies-tab__empty']}>{t('appAdmin.EditTabsComponent.noStudies')}</p>
                    )}
                </div>

            ) : (
                <div className={styles['studies-editor']}>
                    {/* Heading with counter pill */}
                    <div className={styles['studies-editor__heading']}>
                        <div>
                            <h2 className={styles['studies-editor__title']}>
                                {t('appAdmin.EditTabsComponent.studiesEditorTitle', 'Estudios')}
                            </h2>
                            <p className={styles['studies-editor__intro']}>
                                {t('appAdmin.EditTabsComponent.studiesEditorIntro', 'Los estudios y certificaciones ya cargados pueden cambiarse de posición. El orden que definas acá será el que se utilice en el perfil público.')}
                            </p>
                        </div>
                        <span className={styles['studies-editor__count']}>
                            {savedStudies.length} {savedStudies.length === 1
                                ? t('appAdmin.EditTabsComponent.studyCount_one', 'estudio')
                                : t('appAdmin.EditTabsComponent.studyCount_other', 'estudios')}
                        </span>
                    </div>

                    {/* Order note */}
                    <div className={styles['studies-editor__order-note']}>
                        <strong>{t('appAdmin.EditTabsComponent.orderNoteTitle', 'Orden manual:')}</strong>{' '}
                        {t('appAdmin.EditTabsComponent.orderNoteBody', 'usá las flechas ↑ y ↓ para priorizar la formación que querés mostrar primero.')}
                    </div>

                    {/* Add button */}
                    <div className={styles['studies-editor__header']}>
                        <ButtonUi
                            variant="outlined"
                            onClick={() => onStudiesChange?.(arr => [
                                { name: '', institute: '', type: { code: '', label: '' }, type_other: '', specialty: null, declared_specialty: '', declared_specialty_status: '', date_end: '', url_certificate: null, _local_id: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'local-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now()) },
                                ...arr
                            ])}
                            startIcon={<AddRoundedIcon />}
                        >
                            {t('appAdmin.EditTabsComponent.addStudy')}
                        </ButtonUi>
                    </div>

                    {errors?.['studies'] && <div className={styles['studies-editor__error-msg']}>{errors['studies']}</div>}

                    {/* New (unsaved) studies as inline forms */}
                    {newStudies.length > 0 && (
                        <div className={styles['studies-editor__grid']} style={{ marginBottom: savedStudies.length > 0 ? '20px' : undefined }}>
                            {(studies || []).map((s, i) => (
                                !s.delete && !s.uid && (
                                    <article key={(s as any)._local_id || `fallback-${i}`} className={styles['edit-card']}>
                                        <div className={styles['edit-card__actions-wrap']}>
                                            <button
                                                className={styles['edit-card__reorder-btn']}
                                                onClick={() => handleMoveUp(i)}
                                                disabled={isFirstActive(i)}
                                                type="button"
                                            >
                                                <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
                                            </button>
                                            <button
                                                className={styles['edit-card__reorder-btn']}
                                                onClick={() => handleMoveDown(i)}
                                                disabled={isLastActive(i)}
                                                type="button"
                                            >
                                                <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
                                            </button>
                                            <button
                                                className={styles['edit-card__delete-btn']}
                                                onClick={() => handleOpenConfirm({ index: i, type: 'study' })}
                                                type="button"
                                            >
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                            </button>
                                        </div>

                                        <div className={styles['edit-card__form-grid']}>
                                            <TextFieldUi
                                                label={t('appAdmin.EditTabsComponent.titleStudy')}
                                                placeholder={t('appAdmin.EditTabsComponent.titleStudyPlaceholder')}
                                                helperText={t('appAdmin.EditTabsComponent.titleStudyHelperText')}
                                                value={s.name || ''}
                                                onChange={e => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                                            />

                                            <TextFieldUi
                                                label={t('appAdmin.EditTabsComponent.institute')}
                                                placeholder={t('appAdmin.EditTabsComponent.institutePlaceholder')}
                                                value={s.institute || ''}
                                                onChange={e => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, institute: e.target.value } : x))}
                                            />

                                            <div className={styles['edit-card__row']}>
                                                {(() => {
                                                    const isStandard = !s.type?.code || (studiesTypes.some(st => st.code === s.type?.code) && s.type?.code !== 'Otro');

                                                    if (isStandard) {
                                                        return (
                                                            <SelectUi
                                                                label={t('appAdmin.EditTabsComponent.type')}
                                                                value={s.type?.code || ''}
                                                                onChange={e => {
                                                                    const newType = studiesTypes.find(t => t.code === e.target.value);
                                                                    onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? {
                                                                        ...x,
                                                                        type: newType,
                                                                        type_other: newType?.code === 'Otro' ? (x.type_other || '') : ''
                                                                    } : x));
                                                                }}
                                                            >
                                                                {studiesTypes.map(type => <MenuItem key={type.code} value={type.code}>{type.label}</MenuItem>)}
                                                            </SelectUi>
                                                        );
                                                    }

                                                    return (
                                                        <TextFieldUi
                                                            label={t('appAdmin.EditTabsComponent.otherType', 'Especifique el tipo')}
                                                            value={s.type_other || (s.type?.code !== 'Otro' ? s.type?.code : '') || ''}
                                                            onChange={e => {
                                                                const val = e.target.value.slice(0, 50);
                                                                onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? {
                                                                    ...x,
                                                                    type_other: val,
                                                                    type: { code: val, label: val }
                                                                } : x));
                                                            }}
                                                            error={!!errors?.[`studies_type_other_${i}`]}
                                                            helperText={errors?.[`studies_type_other_${i}`] || `${(s.type_other || (s.type?.code !== 'Otro' ? s.type?.code : '') || '').length}/50`}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <Tooltip title={t('appAdmin.EditTabsComponent.backToSelection', 'Regresar al selector')}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, type: { code: '', label: '' }, type_other: '' } : x))}
                                                                            sx={{ color: '#6B8277' }}
                                                                        >
                                                                            <UndoRoundedIcon sx={{ fontSize: 18 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )
                                                            }}
                                                        />
                                                    );
                                                })()}

                                                {loadingTags ? (
                                                    <div className={styles['edit-card__loading']}><CircularProgress size={20} /></div>
                                                ) : (
                                                    <MultiSelectUi
                                                        label={t('appAdmin.EditProfessionalInfo.specialtiesTags')}
                                                        options={availableTags.map(t => ({ uid: t.uid, name: t.name }))}
                                                        value={s.specialty ? [s.specialty.uid] : []}
                                                        maxLimit={1}
                                                        onChange={(selected) => {
                                                            const specialty = availableTags.find(t => t.uid === selected[0]);
                                                            onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, specialty: specialty || null, declared_specialty: '', declared_specialty_status: '' } : x));
                                                        }}
                                                    />
                                                )}
                                            </div>
                                            {!s.specialty && (
                                                <div className={styles['edit-card__row']} style={{ marginTop: '-4px' }}>
                                                    <div style={{ gridColumn: '1 / -1' }}>
                                                        <TextFieldUi
                                                            label={t('appAdmin.EditProfessionalInfo.declaredSpecialty', '¿No encontrás tu especialidad? Escribila acá (opcional)')}
                                                            value={s.declared_specialty || ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value;
                                                                onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? {
                                                                    ...x,
                                                                    declared_specialty: val,
                                                                    declared_specialty_status: val.trim() !== '' ? 'pending_indexing' : 'not_associated'
                                                                } : x));
                                                            }}
                                                            fullWidth
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            <div className={styles['edit-card__row']}>
                                                <DateInputUi
                                                    label={`${t('appAdmin.EditTabsComponent.dateEnd', 'Fecha de culminación')} (Opcional)`}
                                                    placeholder="DD/MM/AAAA (Opcional)"
                                                    value={s.date_end ? dayjs(s.date_end) : null}
                                                    onChange={date => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, date_end: date?.format('YYYY-MM-DD') } : x))}
                                                />
                                            </div>

                                            <div className={styles['edit-card__file-section']}>
                                                <label className={styles['edit-card__label-small']}>{t('appAdmin.EditTabsComponent.fileCertificate')}</label>
                                                <div className={styles['edit-card__file-controls']}>
                                                    <input
                                                        type="file"
                                                        accept="image/*,application/pdf"
                                                        id={`file-${i}`}
                                                        style={{ display: 'none' }}
                                                        onChange={e => {
                                                            const file = e.target.files?.[0];
                                                            if (file) onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, url_certificate: file } : x));
                                                        }}
                                                    />
                                                    <ButtonUi variant="outlined" size="small" onClick={() => document.getElementById(`file-${i}`)?.click()}>
                                                        {s.url_certificate ? t('appAdmin.EditTabsComponent.changeFile') : t('appAdmin.EditTabsComponent.uploadFileCertificate')}
                                                    </ButtonUi>

                                                    {s.url_certificate && (
                                                        <button
                                                            type="button"
                                                            className={styles['edit-card__view-link']}
                                                            onClick={() => {
                                                                const url = typeof s.url_certificate === 'string' ? `${process.env.REACT_APP_API_URL}${s.url_certificate}` : URL.createObjectURL(s.url_certificate as File);
                                                                handleOpenViewer(url, s.name || '');
                                                            }}
                                                        >
                                                            {t('appAdmin.EditTabsComponent.viewCertificate')}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                )
                            ))}
                        </div>
                    )}
{/* Saved studies: compact card or expanded inline form */}
                    {savedStudies.length > 0 && (
                        <div className={styles['studies-editor__grid']}>
                            {(studies || []).map((s, i) => {
                                if (s.delete || !s.uid) return null;
                                const visualNumber = savedStudies.indexOf(s) + 1;
                                const isExpanded = expandedStudyUid === s.uid;

                                if (isExpanded) {
                                    // Inline edit form for this saved study
                                    return (
                                        <article key={s.uid} className={styles['edit-card']}>
                                            <div className={styles['edit-card__actions-wrap']}>
                                                <button
                                                    className={styles['edit-card__reorder-btn']}
                                                    onClick={() => handleMoveUp(i)}
                                                    disabled={isFirstActive(i)}
                                                    type="button"
                                                >
                                                    <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
                                                </button>
                                                <button
                                                    className={styles['edit-card__reorder-btn']}
                                                    onClick={() => handleMoveDown(i)}
                                                    disabled={isLastActive(i)}
                                                    type="button"
                                                >
                                                    <ArrowDownwardRoundedIcon sx={{ fontSize: 18 }} />
                                                </button>
                                                <button
                                                    className={styles['edit-card__collapse-btn']}
                                                    type="button"
                                                    title={t('common.collapse', 'Contraer')}
                                                    onClick={() => toggleExpanded(s.uid!)}
                                                >
                                                    ✕
                                                </button>
                                                <button
                                                    className={styles['edit-card__delete-btn']}
                                                    onClick={() => handleOpenConfirm({ index: i, type: 'study' })}
                                                    type="button"
                                                >
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                </button>
                                            </div>

                                            <div className={styles['edit-card__form-grid']}>
                                                <TextFieldUi
                                                    label={t('appAdmin.EditTabsComponent.titleStudy')}
                                                    placeholder={t('appAdmin.EditTabsComponent.titleStudyPlaceholder')}
                                                    helperText={t('appAdmin.EditTabsComponent.titleStudyHelperText')}
                                                    value={s.name || ''}
                                                    onChange={e => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                                                />
                                                <TextFieldUi
                                                    label={t('appAdmin.EditTabsComponent.institute')}
                                                    placeholder={t('appAdmin.EditTabsComponent.institutePlaceholder')}
                                                    value={s.institute || ''}
                                                    onChange={e => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, institute: e.target.value } : x))}
                                                />
                                                <div className={styles['edit-card__row']}>
                                                    {(() => {
                                                        const isStandard = !s.type?.code || (studiesTypes.some(st => st.code === s.type?.code) && s.type?.code !== 'Otro');
                                                        if (isStandard) {
                                                            return (
                                                                <SelectUi
                                                                    label={t('appAdmin.EditTabsComponent.type')}
                                                                    value={s.type?.code || ''}
                                                                    onChange={e => {
                                                                        const newType = studiesTypes.find(t => t.code === e.target.value);
                                                                        onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, type: newType, type_other: newType?.code === 'Otro' ? (x.type_other || '') : '' } : x));
                                                                    }}
                                                                >
                                                                    {studiesTypes.map(type => <MenuItem key={type.code} value={type.code}>{type.label}</MenuItem>)}
                                                                </SelectUi>
                                                            );
                                                        }
                                                        return (
                                                            <TextFieldUi
                                                                label={t('appAdmin.EditTabsComponent.otherType', 'Especifique el tipo')}
                                                                value={s.type_other || (s.type?.code !== 'Otro' ? s.type?.code : '') || ''}
                                                                onChange={e => {
                                                                    const val = e.target.value.slice(0, 50);
                                                                    onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, type_other: val, type: { code: val, label: val } } : x));
                                                                }}
                                                                InputProps={{
                                                                    endAdornment: (
                                                                        <Tooltip title={t('appAdmin.EditTabsComponent.backToSelection', 'Regresar al selector')}>
                                                                            <IconButton size="small" onClick={() => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, type: { code: '', label: '' }, type_other: '' } : x))} sx={{ color: '#6B8277' }}>
                                                                                <UndoRoundedIcon sx={{ fontSize: 18 }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )
                                                                }}
                                                            />
                                                        );
                                                    })()}
                                                    {loadingTags ? (
                                                        <div className={styles['edit-card__loading']}><CircularProgress size={20} /></div>
                                                    ) : (
                                                        <MultiSelectUi
                                                            label={t('appAdmin.EditProfessionalInfo.specialtiesTags')}
                                                            options={availableTags.map(t => ({ uid: t.uid, name: t.name }))}
                                                            value={s.specialty ? [s.specialty.uid] : []}
                                                            maxLimit={1}
                                                            onChange={(selected) => {
                                                                const specialty = availableTags.find(t => t.uid === selected[0]);
                                                                onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, specialty: specialty || null, declared_specialty: '', declared_specialty_status: '' } : x));
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                {!s.specialty && (
                                                    <div className={styles['edit-card__row']} style={{ marginTop: '-4px' }}>
                                                        <div style={{ gridColumn: '1 / -1' }}>
                                                            <TextFieldUi
                                                                label={t('appAdmin.EditProfessionalInfo.declaredSpecialty', '¿No encontrás tu especialidad? Escribila acá (opcional)')}
                                                                value={s.declared_specialty || ''}
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? {
                                                                        ...x,
                                                                        declared_specialty: val,
                                                                        declared_specialty_status: val.trim() !== '' ? 'pending_indexing' : 'not_associated'
                                                                    } : x));
                                                                }}
                                                                fullWidth
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                <div className={styles['edit-card__row']}>
                                                    <DateInputUi
                                                        label={`${t('appAdmin.EditTabsComponent.dateEnd', 'Fecha de culminación')} (Opcional)`}
                                                        placeholder="DD/MM/AAAA (Opcional)"
                                                        value={s.date_end ? dayjs(s.date_end) : null}
                                                        onChange={date => onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, date_end: date?.format('YYYY-MM-DD') } : x))}
                                                    />
                                                </div>
                                                <div className={styles['edit-card__file-section']}>
                                                    <label className={styles['edit-card__label-small']}>{t('appAdmin.EditTabsComponent.fileCertificate')}</label>
                                                    <div className={styles['edit-card__file-controls']}>
                                                        <input type="file" accept="image/*,application/pdf" id={`file-${i}`} style={{ display: 'none' }}
                                                            onChange={e => { const file = e.target.files?.[0]; if (file) onStudiesChange?.(arr => arr.map((x, idx) => idx === i ? { ...x, url_certificate: file } : x)); }}
                                                        />
                                                        <ButtonUi variant="outlined" size="small" onClick={() => document.getElementById(`file-${i}`)?.click()}>
                                                            {s.url_certificate ? t('appAdmin.EditTabsComponent.changeFile') : t('appAdmin.EditTabsComponent.uploadFileCertificate')}
                                                        </ButtonUi>
                                                        {s.url_certificate && (
                                                            <button type="button" className={styles['edit-card__view-link']}
                                                                onClick={() => { const url = typeof s.url_certificate === 'string' ? `${process.env.REACT_APP_API_URL}${s.url_certificate}` : URL.createObjectURL(s.url_certificate as File); handleOpenViewer(url, s.name || ''); }}
                                                            >
                                                                {t('appAdmin.EditTabsComponent.viewCertificate')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                }

                                return (
                                    <StudyEditorCard
                                        key={s.uid}
                                        study={s}
                                        index={visualNumber}
                                        isFirst={isFirstActive(i)}
                                        isLast={isLastActive(i)}
                                        onMoveUp={() => handleMoveUp(i)}
                                        onMoveDown={() => handleMoveDown(i)}
                                        onDelete={() => handleOpenConfirm({ index: i, type: 'study' })}
                                        onEdit={() => toggleExpanded(s.uid!)}
                                        onViewCertificate={handleOpenViewer}
                                    />
                                );
                            })}
                        </div>
                    )}


                                    </div>
            )}
        </div>
    );
};